<template>
    <div v-if="isMounted" class="row">
        <were-changes-alert :were-changes="wereChanges" @save="save" />

        <div class="col-xl-9 mb-3">
            <div class="card h-100">
                <div class="card-body">
                    <component
                        :is="currentContentComponent.key"
                        ref="contentComponent"
                        v-model="value"
                        :server-errors="serverErrors"
                        @dirty-change="onDirtyChange"
                        @campus-change="onCampusChange"
                    />
                </div>
            </div>
        </div>
        <div class="col-xl-3 mb-3">
            <div class="card">
                <div class="card-body">
                    <div class="mb-3">
                        <a
                            class="btn btn-primary btn-static waves-effect waves-light mr-1 mb-1"
                            :href="previewUrl"
                            target="_blank"
                            @click="onPreviewButtonClick"
                        >
                            <i class="far fa-eye" />
                            Preview
                        </a>
                        <button
                            class="btn btn-success btn-static waves-effect waves-light mb-1"
                            @click="save"
                        >
                            <i class="fas fa-save" />
                            Save
                        </button>
                    </div>

                    <item-information
                        :sections="sections"
                        :item="information"
                        :options="{ deleteButton: false }"
                        @meta-change="onMetaChange"
                    />
                </div>
            </div>
        </div>
    </div>
    <skeleton-loader v-else />
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import ContentPages from '@/components/contents';
import ItemInformation from '@/components/elements/ItemInformation';
import WereChangesAlert from '@/components/elements/WereChangesAlert';

export default {
    components: {
        ...ContentPages,
        ItemInformation,
        WereChangesAlert
    },

    props: {
        currentContentComponent: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            initMetaStringify: '',
            isContentDirty: false,
            isMounted: false,
            information: {},
            contentName: '',
            contentKey: '',
            previewUrl: '',
            value: {},
            serverErrors: []
        };
    },

    computed: {
        wereMetaChanges() {
            return (
                this.isItPage &&
                JSON.stringify(this.information.meta) !== this.initMetaStringify
            );
        },
        wereChanges() {
            return this.isContentDirty || this.wereMetaChanges;
        },
        sections() {
            const sections = { status: true };

            if (this.isItPage) {
                sections.meta = true;
            }

            return sections;
        }
    },

    created() {
        this.contentKey = this.currentContentComponent.key;
        this.contentName = this.currentContentComponent.name;
    },

    async mounted() {
        try {
            const { value, meta, createdAt, updatedAt, type, url } =
                await this.getContent(this.contentKey);

            this.isItPage = type === 'page';
            this.previewUrl = `${process.env.VUE_APP_FRONTEND_URL}/${url}`;
            this.value = { ...value };
            this.information = {
                createdAt,
                updatedAt,
                meta
            };

            if (this.isItPage) {
                this.initMetaStringify = JSON.stringify(meta);
            }

            if (this.contentKey === 'aboutPage') {
                const { id: campusId } = await this.getDefaultCampus();

                const commonGroups = await this.getGroups({
                    isCommon: true
                });
                const groups = await this.getGroups({
                    campusId
                });

                this.value = { ...value, commonGroups, groups, campusId };
            }

            this.isMounted = true;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            getContent: 'contents/show',
            updateContent: 'contents/update',
            getGroups: 'contents/getGroups',
            updateGroups: 'contents/updateGroups',
            getDefaultCampus: 'campuses/getDefault'
        }),

        async save() {
            this.serverErrors = [];

            const { contentComponent } = this.$refs;

            if (contentComponent.$v) {
                contentComponent.$v.$touch();

                if (contentComponent.$v.$invalid) {
                    this.$toasterError('Recheck your form');

                    return;
                }
            }

            try {
                contentComponent.beforeSave
                    ? contentComponent.beforeSave()
                    : {};

                this.value = { ...contentComponent.content };

                const { commonGroups, groups, campusId, ...data } = this.value;

                const content = await this.updateContent({
                    id: this.contentKey,
                    data: { ...data, meta: this.information.meta }
                });

                if (this.contentKey === 'aboutPage') {
                    await this.updateGroups({
                        groups: commonGroups,
                        campusId: null
                    });
                    await this.updateGroups({ groups, campusId });
                }

                this.information.updatedAt = content.updatedAt;

                this.initMetaStringify = JSON.stringify(this.information.meta);

                this.$toaster(this.contentName + ' has been updated');

                contentComponent.afterSave ? contentComponent.afterSave() : {};
            } catch (error) {
                console.error(error);

                const { response } = error;
                let errors = ['Something went wrong!'];

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        errors = data.errors.map(error => {
                            if (error instanceof Object) {
                                return `${error.param}: ${error.message}`;
                            }

                            return error;
                        });
                    }
                }

                this.$toasterError(errors.join(' '));
            }
        },

        onMetaChange(meta) {
            this.information.meta = { ...meta };
        },

        onPreviewButtonClick(e) {
            if (this.wereChanges) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onDirtyChange(v) {
            this.isContentDirty = v;
        },

        async onCampusChange(campusId) {
            const groups = await this.getGroups({ campusId });

            this.value = { ...this.value, groups, campusId };
        }
    }
};
</script>
